/**
 * renderPostCard — turns a community post into a polished, on-brand 1080×1350
 * (Instagram portrait) PNG, drawn on a canvas (no dependencies). Premium dark +
 * gold (#E2DD9F), Bebas/DM Sans, BAM microscope mark + branding. Returns
 * { url, blob }.
 */

const GOLD = "#E2DD9F";
const W = 1080, H = 1350;
const PAD = 96;
const MARK_SRC = (process.env.PUBLIC_URL || "") + "/bam-mark.png";

function initialsOf(name = "") {
  return (name.trim().split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("") || "C").toUpperCase();
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapLines(ctx, text, maxWidth) {
  const out = [];
  for (const para of String(text || "").split("\n")) {
    if (!para.trim()) { out.push(""); continue; }
    let line = "";
    for (const word of para.split(/\s+/)) {
      const test = line ? line + " " + word : word;
      if (ctx.measureText(test).width > maxWidth && line) { out.push(line); line = word; }
      else line = test;
    }
    if (line) out.push(line);
  }
  return out;
}

export async function renderPostCard({ author = "Coach", body = "", handle = "", role = "" }) {
  // Make sure the brand faces are ready before measuring/drawing text.
  try {
    await Promise.all([
      document.fonts.load("500 60px 'DM Sans'"),
      document.fonts.load("700 34px 'DM Sans'"),
      document.fonts.load("400 96px 'Bebas Neue'"),
    ]);
    await document.fonts.ready;
  } catch { /* fonts may already be cached */ }

  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background + gold glow
  ctx.fillStyle = "#0A0A0A"; ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 140, 60, W / 2, 140, 760);
  glow.addColorStop(0, "rgba(226,221,159,0.12)");
  glow.addColorStop(1, "rgba(226,221,159,0)");
  ctx.fillStyle = glow; ctx.fillRect(0, 0, W, H);

  const mark = await loadImage(MARK_SRC).catch(() => null);

  // Header: mark + wordmark
  ctx.textBaseline = "alphabetic";
  if (mark) ctx.drawImage(mark, PAD, 96, 54, 73);
  ctx.fillStyle = GOLD;
  ctx.font = "400 42px 'Bebas Neue'";
  ctx.fillText("BAM COACHES", PAD + (mark ? 72 : 0), 150);

  // Gold rule
  ctx.fillStyle = "rgba(226,221,159,0.55)";
  ctx.fillRect(PAD, 210, 72, 5);

  // Big opening quote
  ctx.fillStyle = "rgba(226,221,159,0.22)";
  ctx.font = "400 200px 'Bebas Neue'";
  ctx.fillText("“", PAD - 8, 360);

  // Body — fit font size to the available band
  const bodyTop = 420, bodyBottom = 1130, maxWidth = W - PAD * 2;
  ctx.textBaseline = "top";
  let fontSize = 62, lines = [];
  while (fontSize >= 30) {
    ctx.font = `500 ${fontSize}px 'DM Sans'`;
    lines = wrapLines(ctx, body, maxWidth);
    if (lines.length * (fontSize * 1.34) <= bodyBottom - bodyTop) break;
    fontSize -= 3;
  }
  ctx.font = `500 ${fontSize}px 'DM Sans'`;
  const lineH = fontSize * 1.34;
  const maxLines = Math.max(1, Math.floor((bodyBottom - bodyTop) / lineH));
  const draw = lines.slice(0, maxLines);
  if (lines.length > maxLines && draw.length) draw[draw.length - 1] = draw[draw.length - 1].replace(/\s+\S*$/, "") + "…";
  ctx.fillStyle = "#f0f0f0";
  draw.forEach((ln, i) => ctx.fillText(ln, PAD, bodyTop + i * lineH));

  // Footer author block
  const fy = 1200;
  ctx.beginPath(); ctx.arc(PAD + 34, fy + 26, 34, 0, Math.PI * 2); ctx.closePath();
  ctx.fillStyle = "rgba(226,221,159,0.16)"; ctx.fill();
  ctx.fillStyle = GOLD; ctx.font = "700 26px 'DM Sans'";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(initialsOf(author), PAD + 34, fy + 28);
  ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#fff"; ctx.font = "700 34px 'DM Sans'";
  ctx.fillText(author, PAD + 88, fy + 20);
  ctx.fillStyle = "#888"; ctx.font = "400 26px 'DM Sans'";
  ctx.fillText(handle ? `@${handle}` : (role || "BAM Coach"), PAD + 88, fy + 58);

  // Brand footer (right)
  ctx.fillStyle = "rgba(226,221,159,0.7)"; ctx.font = "400 30px 'Bebas Neue'";
  ctx.textAlign = "right";
  ctx.fillText("BYANYMEANSCOACHES.COM", W - PAD, fy + 46);
  ctx.textAlign = "left";

  // Faint watermark mark
  if (mark) { ctx.globalAlpha = 0.05; ctx.drawImage(mark, W - 250, H - 360, 210, 284); ctx.globalAlpha = 1; }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve({ blob, url: URL.createObjectURL(blob) }), "image/png");
  });
}
